import React, { FC, useEffect } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { Pressable, StyleSheet } from 'react-native';

import { accordionItemTestIds } from 'components/accordion/constants';
import { IUseAccordionItemProps } from 'components/accordion/types';
import { useAccordionItem } from 'components/accordion/use-accordion-item';
import { Box, Typography } from 'components/ui';
import { getDesignToken, spacing } from 'styles/constants';

const {
  accordionActivatorButtonTestId,
  accordionItemContentTestId,
  accordionItemTestId,
  accordionItemTitleTestId,
} = accordionItemTestIds;

export interface ISignupAccordionProps {
  title: string;
  isOpen?: boolean;
  disabled?: boolean;
  id: string;
}

export const SignupAccordion: FC<React.PropsWithChildren<
  IUseAccordionItemProps & ISignupAccordionProps
>> = ({ title, onInteract, isOpen, children, disabled = false, id, ...props }) => {
  const { handleToggle, isActive } = useAccordionItem({ onInteract });

  useEffect(() => {
    handleToggle(isOpen);
  }, [handleToggle, isOpen]);

  if (!children) {
    return null;
  }

  return (
    <Box {...props} testID={accordionItemTestId}>
      <Pressable
        onPress={() => handleToggle()}
        disabled={disabled}
        testID={accordionActivatorButtonTestId}
        accessibilityRole="button"
        accessibilityState={{ disabled, expanded: isActive }}
        aria-expanded={isActive}
        aria-controls={`${id}-section`}
        style={styles.activatorButton}
      >
        <Box style={styles.listItemContainer}>
          <Typography
            nativeID={id}
            testID={accordionItemTitleTestId}
            variant="copyTwo"
            color={getDesignToken('text-default')}
            style={styles.title}
          >
            {title}
          </Typography>
          <Icon variant={isActive ? 'collapse' : 'select'} alignSelf={'flex-end'} />
        </Box>
      </Pressable>
      {isActive && (
        <Box testID={accordionItemContentTestId} nativeID={`${id}-section`} aria-labelledby={id}>
          {children}
        </Box>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  activatorButton: {
    backgroundColor: 'transparent',
    padding: 0,
    width: '100%',
  },
  listItemContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: spacing.$6,
  },
  title: {
    alignSelf: 'flex-start',
    margin: 0,
    marginTop: spacing.$1,
  },
});
